import { useState, useEffect } from "react";
import ComposePanel from "../outlook/ComposePanel";
import Icon from "../ui/Icon";
import { BRAND, THEMES } from "../../config/constants";
import { msCall } from "../../utils/api";
import { btnP, btnS } from "../../utils/styles";
import { sanitizeEmailHtml, looksLikeHtml } from "../../utils/safeHtml";

export default function ClientEmailsTab({ client, theme }) {
  const t = THEMES[theme];
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [compose,       setCompose]       = useState(false);
  const [emails,        setEmails]        = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [msConnected,   setMsConnected]   = useState(false);
  const [sendLoading,   setSendLoading]   = useState(false);
  const [sendError,     setSendError]     = useState("");

  useEffect(() => {
    checkAndLoad();
  }, [client.id]);

  async function checkAndLoad() {
    try {
      const status = await msCall("msGetStatus", {}, "GET");
      setMsConnected(status.connected);
      if (status.connected) fetchClientEmails();
    } catch { setMsConnected(false); }
  }

  async function fetchClientEmails() {
    setLoading(true);
    try {
      const data = await msCall("msFetchEmails", { top: 200 }, "GET");
      const all = data.emails || [];
      const clientAddrs = [client.email, client.spouseEmail].filter(Boolean).map(e => e.toLowerCase());
      const filtered = all.filter(e => {
        const from = e.from?.emailAddress?.address?.toLowerCase() || "";
        const tos = (e.messages || []).flatMap(m => (m.toRecipients||[]).map(r => r.emailAddress?.address?.toLowerCase()||""));
        return clientAddrs.some(addr => from === addr || tos.includes(addr));
      });
      setEmails(filtered);
    } catch (e) { console.error("fetchClientEmails:", e.message); }
    finally { setLoading(false); }
  }

  function normalizeEmail(e) {
    const from = e.from?.emailAddress;
    return {
      id: e.id,
      from: from?.address || "",
      fromName: from?.name || from?.address || "Unknown",
      subject: e.subject || "(no subject)",
      preview: e.bodyPreview || "",
      date: e.receivedDateTime ? new Date(e.receivedDateTime).toLocaleDateString() : "",
      time: e.receivedDateTime ? new Date(e.receivedDateTime).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"}) : "",
      read: e.isRead,
      thread: e.threadCount || 1,
      messages: (e.messages || []).map(m => ({
        from: m.from?.emailAddress?.address || "",
        fromName: m.from?.emailAddress?.name || m.from?.emailAddress?.address || "Unknown",
        date: m.receivedDateTime ? new Date(m.receivedDateTime).toLocaleString([], {month:"short",day:"numeric",year:"numeric",hour:"2-digit",minute:"2-digit"}) : "",
        body: m.body || m.bodyPreview || "",
      })),
    };
  }

  async function handleSend({ to, cc, bcc, subject, body }) {
    setSendLoading(true); setSendError("");
    try {
      await msCall("msSendEmail", { to, cc: cc||undefined, bcc: bcc||undefined, subject, body });
      setCompose(false);
      fetchClientEmails();
    } catch (e) { setSendError(e.message); }
    finally { setSendLoading(false); }
  }

  const displayEmails = msConnected ? emails.map(normalizeEmail) : [];

  const selected = selectedEmail ? displayEmails.find(e => e.id === selectedEmail) : null;
  const thread = selected
    ? ((selected.messages && selected.messages.length > 0)
        ? selected.messages
        : ([{ from:selected.from, fromName:selected.fromName, date:`${selected.date} · ${selected.time}`, body:selected.preview+"..." }]))
    : [];

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <div style={{fontSize:13,color:t.textSub}}>Emails to/from {client.name}{client.spouseEmail?` & ${client.spouseName||"spouse"}`:""}</div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {msConnected
            ? <div style={{display:"inline-flex",alignItems:"center",gap:6,background:theme==="dark"?"#0a1a0a":"#f0faf0",border:"1px solid #8DC63F40",borderRadius:6,padding:"4px 10px",fontSize:11,color:BRAND.green,fontWeight:700}}>✓ Outlook Connected</div>
            : <div style={{display:"inline-flex",alignItems:"center",gap:6,background:theme==="dark"?"#1a1200":"#fffbeb",border:"1px solid #f59e0b40",borderRadius:6,padding:"4px 10px",fontSize:11,color:"#f59e0b",fontWeight:700}}>⚠ Connect Outlook to sync</div>
          }
          <button onClick={()=>setCompose(true)} style={{...btnP,display:"flex",alignItems:"center",gap:6}}>
            <Icon name="plus" size={13} color="#fff"/>Compose
          </button>
        </div>
      </div>

      {loading && <div style={{textAlign:"center",padding:"30px",color:t.textMuted,fontSize:13}}>Loading emails...</div>}

      {!loading && displayEmails.length === 0 && (
        <div style={{textAlign:"center",padding:"40px 20px",border:`2px dashed ${t.border}`,borderRadius:12,color:t.textMuted}}>
          <div style={{fontSize:32,marginBottom:10}}>📭</div>
          <div style={{fontWeight:700,marginBottom:6,color:t.text}}>No emails found</div>
          <div style={{fontSize:12.5}}>{msConnected ? `No emails found matching ${client.email||"this client"} in your inbox.` : `Connect Outlook to see real emails, or no mock emails match ${client.email||"this client's email"}.`}</div>
        </div>
      )}

      {!loading && displayEmails.length > 0 && (
        <div style={{display:"flex",border:`1px solid ${t.border}`,borderRadius:12,overflow:"hidden",minHeight:400}}>
          {/* Left - email list */}
          <div style={{width:280,flexShrink:0,borderRight:`1px solid ${t.border}`,overflowY:"auto"}}>
            {displayEmails.map(email => {
              const isSel = selectedEmail === email.id;
              return (
                <div key={email.id} onClick={()=>setSelectedEmail(isSel?null:email.id)}
                  style={{padding:"10px 12px",borderBottom:`1px solid ${t.border}`,cursor:"pointer",background:isSel?(theme==="dark"?"#0e1a2e":"#e8f4ff"):(!email.read?(theme==="dark"?"#0a1220":"#f5f9ff"):"transparent"),borderLeft:`3px solid ${isSel?BRAND.blue:(!email.read?BRAND.blue+"80":"transparent")}`}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                    <span style={{fontSize:12,fontWeight:email.read?500:700,color:t.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:160}}>{email.fromName}</span>
                    <span style={{fontSize:10,color:t.textMuted,flexShrink:0}}>{email.date}</span>
                  </div>
                  <div style={{fontSize:11.5,fontWeight:email.read?400:600,color:email.read?t.textSub:t.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",marginBottom:2}}>{email.subject}</div>
                  <div style={{fontSize:11,color:t.textMuted,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{email.preview}</div>
                </div>
              );
            })}
          </div>
          {/* Right - thread view */}
          <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
            {!selected ? (
              <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",color:t.textMuted,fontSize:13}}>Select an email to read</div>
            ) : (
              <>
                <div style={{padding:"14px 18px",borderBottom:`1px solid ${t.border}`}}>
                  <div style={{fontSize:15,fontWeight:800,color:t.text,marginBottom:6}}>{selected.subject}</div>
                  <div style={{display:"flex",gap:8}}>
                    <button onClick={()=>setCompose({replyTo:selected})} style={{...btnP,fontSize:11,padding:"5px 12px",display:"flex",alignItems:"center",gap:4}}><Icon name="arrow" size={11} color="#fff"/>Reply</button>
                    <button onClick={()=>setCompose({replyTo:selected,forward:true})} style={{...btnS(t),fontSize:11,padding:"5px 12px"}}>Forward</button>
                  </div>
                </div>
                <div style={{flex:1,overflowY:"auto"}}>
                  {thread.map((msg,mi)=>(
                    <div key={mi} style={{padding:"16px 18px",borderBottom:`1px solid ${t.border}`,background:mi%2===0?t.bg:t.surface}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <div style={{width:30,height:30,borderRadius:"50%",background:BRAND.blue+"20",border:`2px solid ${BRAND.blue}40`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:BRAND.blue}}>{(msg.fromName||"?")[0]}</div>
                          <div>
                            <div style={{fontSize:12.5,fontWeight:700,color:t.text}}>{msg.fromName}</div>
                            <div style={{fontSize:10.5,color:t.textMuted}}>{msg.from}</div>
                          </div>
                        </div>
                        <span style={{fontSize:11,color:t.textMuted}}>{msg.date}</span>
                      </div>
                      {looksLikeHtml(msg.body)
                        ? <div style={{fontSize:12.5,color:t.text,lineHeight:1.65}} dangerouslySetInnerHTML={{__html:sanitizeEmailHtml(msg.body)}}/>
                        : <div style={{fontSize:12.5,color:t.text,lineHeight:1.65,whiteSpace:"pre-wrap"}}>{msg.body}</div>
                      }
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {compose&&(
        <ComposePanel
          onClose={()=>setCompose(false)}
          theme={theme}
          toEmail={compose.replyTo?.from||client.email||""}
          toName={compose.replyTo?.fromName||client.name||""}
          replyTo={compose.replyTo||null}
          onSend={msConnected ? handleSend : null}
          sendLoading={sendLoading}
          sendError={sendError}
          msConnected={msConnected}
        />
      )}
    </div>
  );
}

// ─── Assign Task Button ──────────────────────────────────────────────────────
